import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';

// Verification review queue: everyone who submitted an identity verification
// and is waiting on you. Approve/reject inline; the badge in the sidebar
// mirrors this count so you never have to hunt through user pages.
// Also a "Verified" tab: everyone who already has the badge, most recently
// approved first, with who approved them (reviewed_by).
const OUTCOME_LABEL = {
  auto_approved: { text: 'AI approved', color: '#4ade80' },
  escalated: { text: 'AI flagged mismatch', color: '#f87171' },
  pending_human_review: { text: 'No AI signal', color: 'rgba(255,255,255,.5)' },
};

const Verifications = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending'); // 'pending' | 'verified' | 'ai_log'
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // Only the newest request may write state, so switching tabs quickly cannot
  // land one tab's rows under another tab's heading.
  const reqSeq = useRef(0);

  // Same fix as Photo Review: a failed load used to render as "no one's
  // waiting", which is a statement about the queue, not about the request.
  const load = async (which) => {
    const seq = (reqSeq.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      if (which === 'ai_log') {
        const res = await api.get('/users/verification/photo-log');
        if (seq !== reqSeq.current) return;
        setLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
      } else {
        const endpoint = which === 'verified' ? '/users/verification/verified' : '/users/verification/pending';
        const res = await api.get(endpoint);
        if (seq !== reqSeq.current) return;
        setUsers(Array.isArray(res.data?.users) ? res.data.users : []);
      }
    } catch (err) {
      if (seq !== reqSeq.current) return;
      console.log(`error loading verifications: ${err}`);
      setUsers([]);
      setLogs([]);
      setLoadError(err?.response?.data?.message || err?.message || 'Request failed');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const decide = async (u, status) => {
    setBusyId(u._id);
    try {
      await api.put(`/users/${u.id || u._id}/verification`, { status });
      setUsers((prev) => prev.filter((x) => x._id !== u._id));
    } catch (e) {
      const missing = e?.response?.data?.missing;
      if (missing?.length) alert(`${e.response.data.message || "Can't verify yet."}\nMissing: ${missing.join(', ')}`);
      else alert('Could not update. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section style={{ padding: '20px 2vw' }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>Verifications</h1>

            <div style={{ display: 'flex', gap: 8, margin: '16px 0 22px' }}>
              <button type="button" onClick={() => setTab('pending')} style={{ borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: tab === 'pending' ? 'none' : '1px solid rgba(255,255,255,.15)', background: tab === 'pending' ? 'linear-gradient(135deg,#0A3FD6,#7229F5)' : 'transparent', color: tab === 'pending' ? '#fff' : 'rgba(255,255,255,.6)' }}>Pending</button>
              <button type="button" onClick={() => setTab('verified')} style={{ borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: tab === 'verified' ? 'none' : '1px solid rgba(255,255,255,.15)', background: tab === 'verified' ? 'linear-gradient(135deg,#0A3FD6,#7229F5)' : 'transparent', color: tab === 'verified' ? '#fff' : 'rgba(255,255,255,.6)' }}>Verified</button>
              <button type="button" onClick={() => setTab('ai_log')} style={{ borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: tab === 'ai_log' ? 'none' : '1px solid rgba(255,255,255,.15)', background: tab === 'ai_log' ? 'linear-gradient(135deg,#0A3FD6,#7229F5)' : 'transparent', color: tab === 'ai_log' ? '#fff' : 'rgba(255,255,255,.6)' }}>AI Log</button>
            </div>

            <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 13, margin: '0 0 22px' }}>
              {loading
                ? 'Loading…'
                : tab === 'pending'
                  ? `${users.length} pending verification${users.length === 1 ? '' : 's'}`
                  : tab === 'verified'
                    ? `${users.length} verified user${users.length === 1 ? '' : 's'}`
                    : `${logs.length} AI verification attempt${logs.length === 1 ? '' : 's'} · every attempt, any outcome`}
            </p>

            {!loading && loadError && (
              <div style={{ background: 'rgba(229,62,62,0.08)', border: '1px solid rgba(229,62,62,0.35)', borderRadius: 14, padding: 28, textAlign: 'center', color: 'rgba(255,255,255,.75)' }}>
                <div style={{ marginBottom: 12 }}>Could not load this list, so it is not the same as it being empty.</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginBottom: 16 }}>{loadError}</div>
                <button type="button" onClick={() => load(tab)} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff', borderRadius: 10, padding: '8px 18px', cursor: 'pointer' }}>
                  Try again
                </button>
              </div>
            )}

            {!loading && !loadError && tab !== 'ai_log' && users.length === 0 && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>
                {tab === 'pending'
                  ? "No one's waiting on verification right now. Submissions land here the moment a user requests to get verified in the app."
                  : "No one's been verified yet."}
              </div>
            )}

            {!loading && !loadError && tab === 'ai_log' && logs.length === 0 && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>
                No AI verification attempts logged yet.
              </div>
            )}

            {tab === 'ai_log' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {logs.map((log) => {
                  const badge = OUTCOME_LABEL[log.outcome] || { text: log.outcome, color: 'rgba(255,255,255,.5)' };
                  return (
                    <div key={log._id} style={{ display: 'flex', gap: 16, alignItems: 'center', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 16, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <img onClick={() => log.selfie_url && setLightbox(log.selfie_url)} src={log.selfie_url || ''} alt="selfie" title="Verification selfie" style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', background: 'rgba(255,255,255,0.05)', cursor: log.selfie_url ? 'pointer' : 'default' }} />
                        <img onClick={() => log.profile_photo_url && setLightbox(log.profile_photo_url)} src={log.profile_photo_url || ''} alt="profile" title="Profile photo at time of check" style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', background: 'rgba(255,255,255,0.05)', cursor: log.profile_photo_url ? 'pointer' : 'default' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{log.name || 'Unnamed'}</div>
                        <div style={{ marginTop: 6 }}>
                          <span style={{ color: badge.color, fontSize: 12, fontWeight: 700 }}>{badge.text}</span>
                        </div>
                        {log.ai_reason && (
                          <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 12, marginTop: 4, maxWidth: 420 }}>{log.ai_reason}</div>
                        )}
                        <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 11, marginTop: 6 }}>{fmt(log.created_at)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button type="button" onClick={() => navigate(`/users/edit/${log.user_id}`)} style={{ borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)' }}>View profile</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {users.map((u) => (
                <div key={u._id} style={{ display: 'flex', gap: 16, alignItems: 'center', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 16, flexWrap: 'wrap' }}>
                  {/* live selfie vs profile photo, side by side to compare */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <img onClick={() => u.verification_photo && setLightbox(u.verification_photo)} src={u.verification_photo || u.photo_url || ''} alt="selfie" title="Verification selfie" style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', background: 'rgba(255,255,255,0.05)', cursor: u.verification_photo ? 'pointer' : 'default' }} />
                    <img onClick={() => u.photo_url && setLightbox(u.photo_url)} src={u.photo_url || ''} alt="profile" title="Profile photo" style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', background: 'rgba(255,255,255,0.05)', cursor: u.photo_url ? 'pointer' : 'default' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{u.name}</div>
                    <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                      {u.ig_handle && <a href={`https://instagram.com/${u.ig_handle}`} target="_blank" rel="noreferrer" style={{ color: '#9d8bff', fontSize: 13, textDecoration: 'none' }}>@{u.ig_handle} · IG ↗</a>}
                      {u.tiktok_handle && <a href={`https://tiktok.com/@${u.tiktok_handle}`} target="_blank" rel="noreferrer" style={{ color: '#9d8bff', fontSize: 13, textDecoration: 'none' }}>@{u.tiktok_handle} · TikTok ↗</a>}
                      {!u.ig_handle && !u.tiktok_handle && <span style={{ color: 'rgba(255,255,255,.35)', fontSize: 12 }}>no handle submitted</span>}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 11, marginTop: 6 }}>
                      {tab === 'pending'
                        ? `Submitted ${fmt(u.submitted_at)}`
                        : `Verified ${fmt(u.reviewed_at)}${u.reviewed_by ? ` by ${u.reviewed_by}` : ''}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button type="button" onClick={() => navigate(`/users/edit/${u.id || u._id}`)} style={{ borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)' }}>View profile</button>
                    {tab === 'pending' ? (
                      <>
                        <button type="button" disabled={busyId === u._id} onClick={() => decide(u, 'rejected')} style={{ borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(248,113,113,.4)', color: '#f87171' }}>Reject</button>
                        <button type="button" disabled={busyId === u._id} onClick={() => decide(u, 'verified')} style={{ borderRadius: 10, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg,#0A3FD6,#7229F5)', color: '#fff' }}>{busyId === u._id ? '…' : '✓ Verify'}</button>
                      </>
                    ) : (
                      <button type="button" disabled={busyId === u._id} onClick={() => decide(u, 'none')} style={{ borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.6)' }}>{busyId === u._id ? '…' : 'Un-verify'}</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}
          </section>
        </article>
      </div>
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'zoom-out' }}>
          <img src={lightbox} alt="full" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 }} />
        </div>
      )}
    </main>
  );
};

export default Verifications;
